import {
  __API_PREFIX,
  __API_SERVER_URL,
  __API_VERSION,
} from '@/api/constants.ts';
import { AccountType } from '@/features/accounts/types.ts';
import { Invoice } from '@/features/invoices/types.ts';
import i18n from '@/i18n/i18n.ts';
import {
  Country,
  PaginatedRequestV2,
  PaginatedResponse,
} from '@arsnovasystems/ars-front-lib';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  Authentication,
  CredentialLogin,
  CredentialLoginToken,
  PartnerInvite,
  PartnerType,
  PartnerUpdate,
  PartnerUpdateType,
} from './types';

export const BASE_API = `${__API_SERVER_URL}${__API_PREFIX}/${__API_VERSION}`;

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    mode: 'cors',
    headers: {
      'Ars-App-Identifier': 'partner-console',
    },
    prepareHeaders: (headers) => {
      headers.set('Ars-Lang-Ui', i18n.language);
    },
    credentials: 'include',
    baseUrl: BASE_API,
  }),

  //declaring the tags
  tagTypes: ['Accounts', 'Partner', 'PaymentMethod', 'FundingPaymentMethod'],

  endpoints: (builder) => ({
    // normal login
    postLogin: builder.mutation<Authentication, CredentialLogin>({
      query: (credentials: CredentialLogin) => ({
        url: '/login',
        method: 'POST',
        body: credentials,
      }),
    }),

    postLoginToken: builder.mutation({
      query: (credentials: CredentialLoginToken) => ({
        url: '/login-token',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['Partner'],
    }),

    logout: builder.mutation<Authentication, void>({
      query: () => '/logout',
    }),

    postPartner: builder.mutation({
      query: (partner: PartnerUpdate) => ({
        url: '/partner',
        method: 'POST',
        body: partner,
      }),
      invalidatesTags: ['Partner', 'PaymentMethod'],
    }),
    putPartner: builder.mutation({
      query: (partner: PartnerUpdateType) => ({
        url: '/partner',
        method: 'PUT',
        body: partner,
      }),
      invalidatesTags: ['Partner'],
    }),
    postPartnerLogo: builder.mutation({
      query: (file: Blob) => {
        const bodyFormData = new FormData();
        bodyFormData.append('file', file);
        return {
          url: '/partner/logo',
          method: 'POST',
          body: bodyFormData,
        };
      },
      invalidatesTags: ['Partner'],
    }),
    getPartnerLogo: builder.query<string | undefined, void>({
      query: () => {
        return {
          // Because the server is serving a base64 image, we do that to build an url to work with in ui
          responseHandler: async (response) => {
            if (
              response.headers.get('content-type') ===
              'application/octet-stream'
            ) {
              return window.URL.createObjectURL(await response.blob());
            }
            return undefined;
          },
          url: '/partner/logo',
          method: 'GET',
          validateStatus: (response) => {
            return response.status < 300 || response.status === 404;
          },
        };
      },
    }),
    postLostCredentials: builder.mutation({
      query: (email: string) => ({
        url: '/lost-credentials',
        method: 'POST',
        body: { value: email },
        validateStatus: (response) => {
          return response.status < 300 || response.status === 401;
        },
      }),
    }),

    getPartner: builder.query<PartnerType, void | string>({
      query: () => ({
        url: `/partner`,
        method: 'GET',
      }),
      providesTags: ['Partner'],
    }),

    getPartnerInvoices: builder.query<
      PaginatedResponse<Invoice>,
      PaginatedRequestV2
    >({
      query: ({ paginate, search }) => ({
        method: 'POST',
        url: `/invoices`,
        body: { paginate, search },
      }),
    }),

    deletePartnerPaymentMethod: builder.mutation<void, string>({
      query: (paymentMethodId) => ({
        method: 'DELETE',
        url: `/post/payment-method/${paymentMethodId}`,
      }),
      invalidatesTags: ['PaymentMethod'],
    }),

    setDefaultPaymentMethod: builder.mutation<void, string>({
      query: (paymentMethodId) => ({
        method: 'PUT',
        url: `/put/set-default-payment-method/${paymentMethodId}`,
      }),
      invalidatesTags: ['PaymentMethod'],
    }),

    deleteDefaultPaymentMethod: builder.mutation<void, void>({
      query: () => ({
        method: 'DELETE',
        url: `/default-payment-method`,
      }),
      invalidatesTags: ['PaymentMethod'],
    }),
    getAccounts: builder.query<
      PaginatedResponse<AccountType>,
      PaginatedRequestV2
    >({
      query: ({ paginate, search }) => ({
        method: 'POST',
        url: `/accounts?page=${paginate.page}`,
        body: { paginate, search },
      }),
      providesTags: ['Accounts'],
    }),

    loginManager: builder.mutation<
      {
        nextStep?: string;
      },
      string
    >({
      query: (userPublicId: string) => ({
        url: `/accounts/${userPublicId}/connect`,
        method: 'POST',
        validateStatus: (response) => {
          // 300 multiple choice is not an error, it is an information that we need to follow the next login step
          return response.status <= 300;
        },
      }),
    }),
    getNotifications: builder.query<
      PaginatedResponse<{ message: string }>,
      void
    >({
      query: () => ({
        url: `/partner/notifications`,
        method: 'GET',
      }),
    }),

    inviteNewUser: builder.mutation<PartnerInvite, unknown>({
      query: (invite: PartnerInvite) => ({
        url: '/accounts/invite',
        method: 'POST',
        body: invite,
      }),
      invalidatesTags: ['Accounts'],
    }),

    resendInvitation: builder.mutation<PartnerInvite, unknown>({
      query: (invite: PartnerInvite) => ({
        url: '/accounts/invite/send',
        method: 'POST',
        body: invite,
      }),
    }),

    cancelInvitation: builder.mutation<AccountType['id'], unknown>({
      query: (inviteId: string) => ({
        url: `/accounts/invite/${inviteId}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['Accounts'],
    }),

    setHasRunTour: builder.mutation({
      invalidatesTags: ['Partner'],
      query: (hasRunTour: boolean) => ({
        url: '/partner/account/has-run-tour',
        method: 'PUT',
        body: {
          value: hasRunTour,
        },
      }),
    }),

    editMyEmail: builder.mutation<{ why: string }, string>({
      query: (email: string) => ({
        url: '/partner/account/email',
        method: 'PUT',
        body: {
          value: email,
        },
      }),
      invalidatesTags: ['Partner'],
    }),
    editMyPassword: builder.mutation<
      { why: string },
      { password: string; noEmail?: boolean }
    >({
      query: ({ password, noEmail }) => ({
        url: `/partner/account/password${noEmail ? '/no-email' : ''}`,
        method: 'PUT',
        body: { password },
      }),
    }),

    editMyDefaultLanguage: builder.mutation<void, string>({
      query: (defaultLang: string) => ({
        url: `/partner/account/preferred-language/${defaultLang}`,
        method: 'PUT',
      }),
    }),

    getCountryCodes: builder.query<
      PaginatedResponse<Country>,
      PaginatedRequestV2
    >({
      query: ({ paginate, search }) => ({
        method: 'POST',
        url: `/country-codes-search`,
        body: { paginate, search },
      }),
    }),
    getPhoneIndicators: builder.query<
      PaginatedResponse<string>,
      PaginatedRequestV2
    >({
      query: ({ paginate, search }) => ({
        method: 'POST',
        url: `/phone-indicator-search`,
        body: {
          paginate,
          search: {
            ...search,
            sortCriteria: 'phoneIndicator',
          },
        },
      }),
    }),
  }),
});

export const {
  useLogoutMutation,
  useGetPartnerQuery,
  usePostLoginMutation,
  usePostLostCredentialsMutation,
  usePostLoginTokenMutation,
  useGetAccountsQuery,
  useGetPartnerInvoicesQuery,
  useDeletePartnerPaymentMethodMutation,
  usePostPartnerLogoMutation,
  usePostPartnerMutation,
  usePutPartnerMutation,
  useLoginManagerMutation,
  useInviteNewUserMutation,
  useResendInvitationMutation,
  useCancelInvitationMutation,
  useSetHasRunTourMutation,
  useEditMyEmailMutation,
  useEditMyDefaultLanguageMutation,
  useSetDefaultPaymentMethodMutation,
  useDeleteDefaultPaymentMethodMutation,
  useEditMyPasswordMutation,
  useLazyGetCountryCodesQuery,
  useGetPartnerLogoQuery,
} = apiSlice;
